import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Client Dashboard — lead review (client profile card)
//
// Every call and form the dashboard counts for a month, with the lead-quality
// category the pipeline gave it (functions-dashboard/lib/classify.js):
// new customer inquiry / existing customer / not a fit / spam or solicitor /
// missed or voicemail. Staff can override any category here; an override is
// marked 'staff' and is never re-classified. Low-confidence AI calls are
// listed first so the review takes minutes, not an hour.
//
// Overrides change the stored rows only; "Rebuild month" re-assembles the
// draft snapshot from them (assembleDashboardMonth). Published months are not
// touched by rebuilds.
// ---------------------------------------------------------------------------

const CATEGORIES = [
  { value: 'new_customer_inquiry', label: 'New customer inquiry', lead: true },
  { value: 'existing_customer', label: 'Existing customer' },
  { value: 'not_a_fit', label: 'Not a fit' },
  { value: 'spam_solicitor', label: 'Spam / solicitor' },
  { value: 'missed_voicemail', label: 'Missed / voicemail' },
];
const LABEL = Object.fromEntries(CATEGORIES.map(c => [c.value, c.label]));
const LOW = 0.7;
const ASSEMBLE_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/assembleDashboardMonth';
const CLASSIFY_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/runDashboardClassify';
const CHANNEL = { google_ads: 'Google Ads', meta_ads: 'Meta Ads', gbp: 'GBP', organic: 'Organic', direct: 'Direct', referral: 'Referral', other: 'Other' };

function recentMonths(n) {
  const out = [];
  const d = new Date();
  d.setUTCDate(1);
  for (let i = 0; i < n; i++) {
    d.setUTCMonth(d.getUTCMonth() - 1);
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

function fmtWhen(ts) {
  const d = ts?.toDate ? ts.toDate() : null;
  return d ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
}

function Tag({ children, strong }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '.3px', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap',
      ...(strong
        ? { background: 'var(--text-primary)', color: 'var(--bg-primary, #fff)', border: '1px solid var(--text-primary)' }
        : { background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--text-muted)' }),
    }}>{children}</span>
  );
}

export default function DashboardLeadsPanel({ clientId, client }) {
  const months = useMemo(() => recentMonths(6), []);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(months[0]);
  const [rows, setRows] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [filter, setFilter] = useState('review'); // review | all | leads
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const q = query(collection(db, 'clients', clientId, 'leads'), where('month', '==', month));
    const unsub = onSnapshot(q, snap => {
      setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => r.leadType === 'call' || r.leadType === 'form'));
    }, () => setRows([]));
    const unsubSnap = onSnapshot(doc(db, 'clients', clientId, 'snapshots', month), s => setSnapshot(s.exists() ? s.data() : null), () => setSnapshot(null));
    return () => { unsub(); unsubSnap(); };
  }, [open, clientId, month]);

  const counted = rows.filter(r => r.leadType === 'form' || r.connected === true || r.missed === true);
  const needsReview = r => r.leadCategorySource === 'ai' && (r.leadCategoryConfidence ?? 1) < LOW;
  const visible = counted
    .filter(r => filter === 'all' ? true : filter === 'leads' ? r.leadCategory === 'new_customer_inquiry' : (needsReview(r) || !r.leadCategory))
    .sort((a, b) => {
      const ra = needsReview(a) ? 0 : 1, rb = needsReview(b) ? 0 : 1;
      if (ra !== rb) return ra - rb;
      return (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0);
    });
  const totals = CATEGORIES.map(c => ({ ...c, n: counted.filter(r => r.leadCategory === c.value).length }));
  const unclassified = counted.filter(r => !r.leadCategory).length;
  const reviewCount = counted.filter(needsReview).length;
  const overrides = counted.filter(r => r.leadCategorySource === 'staff').length;

  const override = async (row, value) => {
    if (!value) return;
    try {
      await updateDoc(doc(db, 'clients', clientId, 'leads', row.id), {
        leadCategory: value,
        leadCategorySource: 'staff',
        leadCategoryConfidence: 1,
        isLead: value === 'new_customer_inquiry',
        leadCategoryOverriddenBy: auth.currentUser?.email || null,
        leadCategoryOverriddenAt: serverTimestamp(),
        leadCategoryAiValue: row.leadCategorySource === 'ai' ? (row.leadCategory || null) : (row.leadCategoryAiValue || null),
      });
      setMsg({ ok: true, text: 'Saved. Rebuild the month when you are done reviewing.' });
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Could not save.' });
    }
  };

  const post = async (url, body, label) => {
    setBusy(label);
    setMsg(null);
    try {
      const token = await auth.currentUser.getIdToken();
      const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      return data;
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Request failed.' });
      return null;
    } finally {
      setBusy(null);
    }
  };

  const rebuild = async () => {
    const data = await post(ASSEMBLE_URL, { clientId, month }, 'rebuild');
    if (data) setMsg({ ok: data.status === 'assembled', text: data.status === 'assembled' ? `Rebuilt ${month}: ${data.counts.calls} calls, ${data.counts.forms} forms.` : `${data.status}${data.missing ? ` — waiting on ${data.missing.join(', ')}` : data.reason ? ` (${data.reason})` : ''}` });
  };
  const classify = async () => {
    const data = await post(CLASSIFY_URL, { clientId, month }, 'classify');
    if (data) setMsg({ ok: data.status === 'success', text: data.status === 'success' ? `Classified ${data.rows} rows (${data.counts?.ai || 0} by AI, ${data.counts?.rule || 0} by rule).` : `${data.status}: ${data.error || ''}` });
  };

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Client Dashboard Leads</div>
          {open && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{reviewCount ? `${reviewCount} to review` : 'nothing to review'}</span>}
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{open ? '▾ Hide' : '▸ Show'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            <select value={month} onChange={e => setMonth(e.target.value)} style={{ padding: '6px 8px', fontSize: 13 }}>
              {months.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={filter} onChange={e => setFilter(e.target.value)} style={{ padding: '6px 8px', fontSize: 13 }}>
              <option value="review">Needs review ({reviewCount + unclassified})</option>
              <option value="leads">New customer inquiries ({totals[0].n})</option>
              <option value="all">All counted contacts ({counted.length})</option>
            </select>
            <button type="button" className="btn btn-secondary" disabled={!!busy} onClick={classify}>{busy === 'classify' ? 'Classifying…' : 'Classify unclassified'}</button>
            <button type="button" className="btn btn-primary" disabled={!!busy} onClick={rebuild}>{busy === 'rebuild' ? 'Rebuilding…' : 'Rebuild month'}</button>
          </div>

          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.7 }}>
            {totals.map(t => <span key={t.value} style={{ marginRight: 12 }}><strong>{t.n}</strong> {t.label}</span>)}
            {unclassified ? <span style={{ marginRight: 12 }}><strong>{unclassified}</strong> unclassified</span> : null}
            {overrides ? <span><strong>{overrides}</strong> staff override{overrides === 1 ? '' : 's'}</span> : null}
            <br />
            Snapshot: {snapshot ? `${snapshot.status}${snapshot.assembledAt?.toDate ? `, built ${snapshot.assembledAt.toDate().toLocaleString()}` : ''}` : 'not built yet'}
            {snapshot?.summary?.leads ? ` · ${snapshot.summary.leads.total} leads in the snapshot` : ''}
          </div>

          {msg && <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>{msg.ok ? '✓ ' : '✕ '}{msg.text}</div>}

          {visible.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nothing here for {month}.</div>}
          {visible.map(r => {
            const review = needsReview(r);
            const text = r.leadType === 'call' ? (r.aiSummary || '(no summary)') : (r.summary || '(empty)');
            const isOpen = !!expanded[r.id];
            return (
              <div key={r.id} style={{ borderTop: '1px solid var(--border-color, #e3e6ea)', padding: '10px 0' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                  <Tag strong={r.leadType === 'call'}>{r.leadType}</Tag>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtWhen(r.timestamp)}</span>
                  <span style={{ fontSize: 12 }}>{CHANNEL[r.channel] || r.channel}</span>
                  {r.leadType === 'call' && r.durationSeconds != null && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{Math.round(r.durationSeconds / 60)} min</span>}
                  {r.leadType === 'call' && r.missed && <Tag>missed</Tag>}
                  {review && <Tag strong>REVIEW · {Math.round((r.leadCategoryConfidence || 0) * 100)}%</Tag>}
                  {r.leadCategorySource === 'staff' && <Tag>staff</Tag>}
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5, cursor: 'pointer' }} onClick={() => setExpanded(e => ({ ...e, [r.id]: !isOpen }))}>
                  {isOpen || text.length <= 180 ? text : `${text.slice(0, 180)}…`}
                </div>
                {r.leadCategoryReason && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>AI: {r.leadCategoryReason}{r.leadCategorySource === 'ai' ? ` (${Math.round((r.leadCategoryConfidence || 0) * 100)}%)` : ''}</div>}
                <div style={{ marginTop: 6 }}>
                  <select value={r.leadCategory || ''} onChange={e => override(r, e.target.value)} style={{ padding: '5px 8px', fontSize: 12 }}>
                    <option value="">— set category —</option>
                    {CATEGORIES.filter(c => r.leadType === 'call' || c.value !== 'missed_voicemail').map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  {r.leadCategory && <span style={{ fontSize: 12, marginLeft: 8, color: 'var(--text-muted)' }}>{LABEL[r.leadCategory]}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
